import { useId, useLayoutEffect, useRef, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import {
  useBindGeoSheetByAxesMutation,
  useBindGeoSheetByGridMutation,
  useDeleteGeoAxisGridMutation,
  useGetGeoAxisGridsQuery,
  useGetGeoSheetAxesQuery,
} from '@shared/api';
import type { GeoAxesBindBody, GeoAxisGrid, GeoScheme } from '@entities/geodesy';
import { Button } from '@shared/ui/Button';
import { IconPlus, IconTrash } from '@shared/ui/icons';
import {
  CellInput,
  CellSelect,
  Check,
  Hint,
  IconButton,
  LinkButton,
  Muted,
  Notice,
  NumInput,
  Row,
  useAction,
} from '@widgets/GeoSchemes';
import { useWizard } from './wizard';

/** Двух пересечений хватает для привязки, третье её проверяет. */
const MIN_CROSSES = 2;
/** Оси объекта запоминаются только по проверенной привязке. */
const MIN_REMEMBER = 3;
/** Название первой сетки объекта, если геодезист его не вписал. */
const DEFAULT_GRID = 'Основные оси';
/** Пункт «Новое здание…» в выборе здания. */
const NEW_GRID = '__new__';

const Box = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(4)};
  border: 1px dashed ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
`;

const Title = styled.h4`
  margin: 0;
  font-size: 13.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

/* Оси объекта: сетка здания, сколько её пересечений на листе, действия. */
const GridList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  margin: 0;
  padding: ${({ theme }) => theme.spacing(3)};
  list-style: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.backgroundElevated};
`;

const GridItem = styled.li`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const GridName = styled.span`
  font-weight: 600;
`;

const GridFacts = styled(Muted)`
  flex: 1 1 200px;
  min-width: 0;
`;

/*
 * Строка пересечения: две оси, X, Y, «убрать». Узкая карточка (телефон или
 * открытое меню) — по ширине самого мастера: оси в первой строке, X и Y во
 * второй, крупные поля, строки разделены.
 */
const CrossRow = styled.div`
  display: grid;
  grid-template-columns:
    minmax(70px, 90px) minmax(70px, 90px) minmax(110px, 1fr) minmax(110px, 1fr)
    auto;
  grid-template-areas: 'a b x y rm';
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};

  & > :nth-child(1) {
    grid-area: a;
  }
  & > :nth-child(2) {
    grid-area: b;
  }
  & > :nth-child(3) {
    grid-area: x;
  }
  & > :nth-child(4) {
    grid-area: y;
  }
  & > :nth-child(5) {
    grid-area: rm;
  }

  @container geo-wizard (max-width: 560px) {
    grid-template-columns: 1fr 1fr auto;
    grid-template-areas:
      'a b rm'
      'x y rm';
    padding-bottom: ${({ theme }) => theme.spacing(2)};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};

    input,
    select {
      height: 40px;
    }
  }
`;

/* Заголовки колонок — узко только для скринридера: подписи в самих полях. */
const HeadRow = styled(CrossRow)`
  @container geo-wizard (max-width: 560px) {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }
`;

const ColHead = styled(Muted)`
  font-size: 12px;
`;

const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const GridNameInput = styled(CellInput)`
  width: 220px;
  max-width: 100%;
`;

interface Draft {
  a: string;
  b: string;
  x: number | null;
  y: number | null;
}

const emptyRow = (): Draft => ({ a: '', b: '', x: null, y: null });
const isComplete = (r: Draft) => r.a !== '' && r.b !== '' && r.x != null && r.y != null;
const isStarted = (r: Draft) => r.a !== '' || r.b !== '' || r.x != null || r.y != null;

/** Чем закончилась последняя привязка — для строки итога. */
interface Done {
  /** Сетка объекта, по которой привязан лист (кнопка «Привязать» у осей объекта). */
  grid: string | null;
  used: string[];
  /** Введённые (или запомненные) пересечения, которые не сошлись с остальными. */
  rejected: string[];
  /** В какую сетку здания запомнены оси. */
  remembered: string | null;
}

/**
 * Привязка листа по осям (шаг 2): программа находит на листе оси с марками в
 * кружках и их пересечения («1-А»), геодезист вводит X, Y двух-трёх
 * пересечений из разбивочного плана — сервер уравнивает, отбрасывает опечатку
 * и пишет обычные точки привязки (`auto:1-А`), невязки — в блоке ниже.
 * Проверенные оси (от трёх пересечений) можно запомнить для объекта ИС — в
 * сетку здания: следующий лист этого здания привяжется сам при загрузке или
 * одной кнопкой «Привязать» у осей объекта.
 */
export function AxesBindPanel({
  draftPoints,
  dirty,
  onDone,
}: {
  /** Сколько точек привязки сейчас в черновике шага — их заменят найденные. */
  draftPoints: number;
  /** Есть несохранённые правки привязки. */
  dirty: boolean;
  /** Сбросить черновик шага — он подхватит точки с сервера. */
  onDone: () => void;
}) {
  const { scheme, readOnly, goTo } = useWizard();
  const sheet = scheme.sheet;
  const isPdf = sheet?.kind === 'pdf';
  const skip = readOnly || !isPdf;
  const { data: axes, isFetching } = useGetGeoSheetAxesQuery(
    { id: scheme.id, fileId: sheet?.file_id ?? '', page: sheet?.page ?? 0 },
    { skip },
  );
  const { data: objectAxes } = useGetGeoAxisGridsQuery(scheme.id, { skip });
  const [bindAxes] = useBindGeoSheetByAxesMutation();
  const [bindGrid] = useBindGeoSheetByGridMutation();
  const [deleteGrid] = useDeleteGeoAxisGridMutation();
  const binding = useAction();
  const [rows, setRows] = useState<Draft[]>(() => [emptyRow(), emptyRow(), emptyRow()]);
  const [focusRow, setFocusRow] = useState<number | null>(null);
  const addRef = useRef<HTMLButtonElement>(null);
  /** `null` — как по умолчанию: запоминать, если у объекта ещё нет осей. */
  const [rememberChoice, setRememberChoice] = useState<boolean | null>(null);
  const [building, setBuilding] = useState('');
  const [newName, setNewName] = useState('');
  const [done, setDone] = useState<Done | null>(null);
  /** Ответ сервера (привязка в JSON), который ещё не дошёл до карточки в кеше. */
  const [pending, setPending] = useState<{ binding: string; done: Done } | null>(null);
  const bindingKey = JSON.stringify(scheme.settings.binding);
  const statusId = useId();

  // Как у автопривязки по номерам: черновик шага сбрасываем, когда карточка
  // в кеше уже с результатом.
  useLayoutEffect(() => {
    if (!pending || pending.binding !== bindingKey) return;
    onDone();
    setDone(pending.done);
    setPending(null);
  }, [pending, bindingKey, onDone]);

  if (skip || !sheet) return null;

  if (!axes) {
    return (
      <Box aria-labelledby="geo-axesbind-title">
        <Title id="geo-axesbind-title">Привязка по осям</Title>
        <Hint>{isFetching ? 'Ищем оси на листе…' : 'Оси листа не загрузились.'}</Hint>
      </Box>
    );
  }
  if (axes.digits.length === 0 || axes.letters.length === 0) {
    // Лист уже привязан (выносками, точками) — осей нет, и говорить не о чем.
    if (scheme.settings.binding.control_points.length >= MIN_CROSSES) return null;
    return (
      <Box aria-labelledby="geo-axesbind-title">
        <Title id="geo-axesbind-title">Привязка по осям</Title>
        <Hint>
          {axes.warnings[0] ?? 'Осей с марками на листе не найдено'} — привяжите лист по точкам
          ниже.
        </Hint>
      </Box>
    );
  }

  const project = objectAxes?.project ?? null;
  const grids = objectAxes?.grids ?? [];
  const onSheet = new Set(axes.crosses.map((c) => c.name));
  const gridOnSheet = (g: GeoAxisGrid) => g.points.filter((p) => onSheet.has(p.name));

  const complete = rows.filter(isComplete);
  const partial = rows.filter((r) => isStarted(r) && !isComplete(r)).length;
  const canRemember = project != null && complete.length >= MIN_REMEMBER;
  const remember = canRemember && (rememberChoice ?? grids.length === 0);
  const gridName =
    grids.length === 0
      ? newName.trim() || DEFAULT_GRID
      : building === NEW_GRID
        ? newName.trim()
        : building;
  const needBuilding = remember && gridName === '';
  const blocked =
    complete.length < MIN_CROSSES
      ? 'Впишите X и Y хотя бы двух пересечений.'
      : needBuilding
        ? 'Выберите здание, чьи это оси, или снимите «Запомнить».'
        : null;

  const range = (list: string[]) =>
    list.length > 1 ? `${list[0]}…${list[list.length - 1]}` : list[0];
  const set = (k: number, patch: Partial<Draft>) =>
    setRows((prev) => prev.map((r, i) => (i === k ? { ...r, ...patch } : r)));

  const confirmReplace = () =>
    draftPoints === 0 ||
    window.confirm(
      dirty
        ? 'Несохранённые точки привязки заменятся точками по осям. Продолжить?'
        : 'Текущие точки привязки заменятся точками по осям. Продолжить?',
    );

  /** Итог по ответу сервера: взятые пересечения — контрольные точки `auto:…`. */
  const finish = (
    res: GeoScheme | null,
    asked: string[],
    rest: Omit<Done, 'used' | 'rejected'>,
  ) => {
    if (!res) return;
    const used = res.settings.binding.control_points.map((c) => c.id.replace(/^auto:/, ''));
    const next: Done = {
      ...rest,
      used,
      rejected: asked.filter((n) => !used.includes(n)),
      remembered: rest.remembered != null && used.length >= MIN_REMEMBER ? rest.remembered : null,
    };
    setPending({ binding: JSON.stringify(res.settings.binding), done: next });
  };

  const runAxes = async (e: FormEvent) => {
    e.preventDefault();
    if (blocked || binding.busy || !confirmReplace()) return;
    setDone(null);
    setPending(null);
    const grid = remember ? gridName : undefined;
    const body: GeoAxesBindBody = {
      points: complete.map((r) => ({ a: r.a, b: r.b, x: r.x ?? 0, y: r.y ?? 0 })),
      grid,
    };
    const res = await binding.runValue(
      () => bindAxes({ id: scheme.id, ...body }).unwrap(),
      'Привязка по осям не выполнена.',
    );
    finish(
      res,
      complete.map((r) => `${r.a}-${r.b}`),
      { grid: null, remembered: grid ?? null },
    );
  };

  const runGrid = async (g: GeoAxisGrid) => {
    if (binding.busy || !confirmReplace()) return;
    setDone(null);
    setPending(null);
    const res = await binding.runValue(
      () => bindGrid({ id: scheme.id, grid_id: g.id }).unwrap(),
      'Привязка по осям объекта не выполнена.',
    );
    finish(
      res,
      gridOnSheet(g).map((p) => p.name),
      { grid: g.name, remembered: null },
    );
  };

  const forget = async (g: GeoAxisGrid) => {
    if (
      !window.confirm(
        `Забыть оси здания «${g.name}» для всего объекта? Листы этого здания перестанут привязываться по ним сами.`,
      )
    )
      return;
    setDone(null);
    await binding.run(
      () => deleteGrid({ id: scheme.id, gridId: g.id }).unwrap(),
      'Оси здания не удалены.',
    );
    if (building === g.name) setBuilding('');
  };

  const addRow = () => {
    setFocusRow(rows.length);
    setRows((prev) => [...prev, emptyRow()]);
  };
  const removeRow = (k: number) => {
    setFocusRow(null);
    setRows((prev) => prev.filter((_, i) => i !== k));
    addRef.current?.focus();
  };

  return (
    <Box aria-labelledby="geo-axesbind-title">
      <Title id="geo-axesbind-title">Привязка по осям</Title>
      {project && grids.length > 0 && (
        <GridList aria-label={`Оси зданий объекта «${project.name}»`}>
          {grids.map((g) => {
            const hits = gridOnSheet(g).length;
            return (
              <GridItem key={g.id}>
                <GridName>{g.name}</GridName>
                <GridFacts>
                  пересечений {g.points.length}, на этом листе — {hits}
                  {hits < MIN_CROSSES ? ' (мало для привязки)' : ''}
                </GridFacts>
                <Button
                  type="button"
                  $variant="secondary"
                  $size="sm"
                  onClick={() => void runGrid(g)}
                  disabled={binding.busy || hits < MIN_CROSSES}
                  aria-label={`Привязать лист по осям здания «${g.name}»`}
                >
                  Привязать
                </Button>
                <IconButton
                  type="button"
                  $danger
                  aria-label={`Забыть оси здания «${g.name}»`}
                  onClick={() => void forget(g)}
                  disabled={binding.busy}
                >
                  <IconTrash size={15} />
                </IconButton>
              </GridItem>
            );
          })}
        </GridList>
      )}
      <Hint>
        На листе найдены оси {range(axes.digits)} и {range(axes.letters)} ({axes.crosses.length}{' '}
        пересечений). Впишите X, Y двух-трёх пересечений из разбивочного плана — щёлкать по листу не
        нужно; третье пересечение проверит привязку.
      </Hint>
      <Form onSubmit={(e) => void runAxes(e)} aria-labelledby="geo-axesbind-title">
        <HeadRow aria-hidden="true">
          <ColHead>Ось-цифра</ColHead>
          <ColHead>Ось-буква</ColHead>
          <ColHead>X (север), м</ColHead>
          <ColHead>Y (восток), м</ColHead>
          <span />
        </HeadRow>
        {rows.map((r, k) => {
          const half = isStarted(r) && !isComplete(r);
          return (
            <CrossRow key={k}>
              <CellSelect
                value={r.a}
                aria-label={`Пересечение ${k + 1}: ось-цифра`}
                aria-invalid={half && r.a === '' ? true : undefined}
                autoFocus={k === focusRow}
                onChange={(e) => set(k, { a: e.target.value })}
              >
                <option value="">1, 2…</option>
                {axes.digits.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </CellSelect>
              <CellSelect
                value={r.b}
                aria-label={`Пересечение ${k + 1}: ось-буква`}
                aria-invalid={half && r.b === '' ? true : undefined}
                onChange={(e) => set(k, { b: e.target.value })}
              >
                <option value="">А, Б…</option>
                {axes.letters.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </CellSelect>
              <NumInput
                variant="cell"
                value={r.x}
                placeholder="X, м"
                aria-label={`Пересечение ${k + 1}: X (север), м`}
                aria-invalid={half && r.x == null ? true : undefined}
                onValue={(v) => set(k, { x: v })}
              />
              <NumInput
                variant="cell"
                value={r.y}
                placeholder="Y, м"
                aria-label={`Пересечение ${k + 1}: Y (восток), м`}
                aria-invalid={half && r.y == null ? true : undefined}
                onValue={(v) => set(k, { y: v })}
              />
              <IconButton
                type="button"
                $danger
                aria-label={`Убрать пересечение ${k + 1}`}
                onClick={() => removeRow(k)}
                disabled={rows.length <= MIN_CROSSES}
              >
                <IconTrash size={15} />
              </IconButton>
            </CrossRow>
          );
        })}
        <Row $gap={2}>
          <Button ref={addRef} type="button" $variant="ghost" $size="sm" onClick={addRow}>
            <IconPlus size={16} /> Пересечение
          </Button>
        </Row>

        {objectAxes &&
          (project ? (
            <Row $gap={2}>
              <Check checked={remember} onChange={setRememberChoice} disabled={!canRemember}>
                Запомнить оси для объекта «{project.name}»
              </Check>
              {remember &&
                (grids.length > 0 ? (
                  <>
                    <CellSelect
                      value={building}
                      aria-label="Здание, чьи это оси"
                      aria-invalid={needBuilding && building === '' ? true : undefined}
                      onChange={(e) => setBuilding(e.target.value)}
                    >
                      <option value="">здание…</option>
                      {grids.map((g) => (
                        <option key={g.id} value={g.name}>
                          {g.name}
                        </option>
                      ))}
                      <option value={NEW_GRID}>Новое здание…</option>
                    </CellSelect>
                    {building === NEW_GRID && (
                      <GridNameInput
                        value={newName}
                        placeholder="например, Корпус 2"
                        maxLength={200}
                        aria-label="Название нового здания"
                        aria-invalid={needBuilding ? true : undefined}
                        onChange={(e) => setNewName(e.target.value)}
                      />
                    )}
                  </>
                ) : (
                  <GridNameInput
                    value={newName}
                    placeholder={`здание: ${DEFAULT_GRID}`}
                    maxLength={200}
                    aria-label="Здание, чьи это оси"
                    onChange={(e) => setNewName(e.target.value)}
                  />
                ))}
              {!canRemember && (
                <Muted>Запоминаются только проверенные оси — от трёх пересечений.</Muted>
              )}
            </Row>
          ) : (
            <Hint>
              Чтобы оси запомнились и следующие листы объекта привязывались сами, выберите «Проект
              ПТО» в реквизитах ИС.{' '}
              <LinkButton type="button" onClick={() => goTo(4)}>
                Реквизиты — шаг 4 →
              </LinkButton>
            </Hint>
          ))}

        <Footer>
          <Button
            type="submit"
            $variant="secondary"
            $size="sm"
            disabled={binding.busy || blocked != null}
            aria-describedby={statusId}
          >
            {binding.busy ? 'Привязываем…' : 'Привязать по осям'}
          </Button>
          <Muted id={statusId} role="status">
            {blocked ??
              `Заполнено пересечений: ${complete.length}${
                partial > 0 ? `; не до конца — ${partial}, в привязку не войдут` : ''
              }.`}
          </Muted>
        </Footer>
      </Form>
      {binding.error && (
        <Notice $tone="danger" role="alert">
          {binding.error}
        </Notice>
      )}
      {done && !binding.error && (
        <Notice $tone="success" role="status">
          {done.grid ? `Лист привязан по осям здания «${done.grid}»` : 'Лист привязан по осям'}:
          взяты {done.used.join(', ')}
          {done.rejected.length > 0 ? `; не сошлось — ${done.rejected.join(', ')}` : ''}. Невязки —
          ниже.
          {done.used.length === MIN_CROSSES
            ? ' По двум пересечениям привязка не проверена — добавьте третье.'
            : ''}
          {done.remembered
            ? ` Оси запомнены для здания «${done.remembered}» — следующие листы привяжутся сами.`
            : ''}
        </Notice>
      )}
    </Box>
  );
}
